import moment from 'moment'
import PropTypes from 'prop-types'
import { Line as LineChart } from 'react-chartjs'
import { sortColumn } from 'reactabular'

import { captureExceptions } from 'utils/SentryHelpers'
import CustomerMetric from '../customer/CustomerMetric'
import * as DateHelpers from 'utils/DateHelpers'
import InboxButton from '../conversation/InboxButton'
import TabbedView from '../TabbedView'
import Table from 'shared-web/src/components/Table'
import TimeSelectorButton from 'components/shared/TimeSelectorButton'
import { ChartOptions, ColorConfig } from 'constants/ChartConfig'

import colors from 'styles/colors.json'
import 'styles/components/Dashboard.styl'

const metricMapping =
  {
    NEW_CONVERSATIONS: {
      display: 'new conversations',
      value(metric) { return metric.sum },
      previousValue(metric) { return metric.previousSum },
      delta(metric) { return DateHelpers.calculateChangePercentage(metric.previousSum, metric.sum) }
    },
    CLOSED_CONVERSATIONS: {
      display: 'closed conversations',
      value(metric) { return metric.sum },
      previousValue(metric) { return metric.previousSum },
      delta(metric) { return DateHelpers.calculateChangePercentage(metric.previousSum, metric.sum) }
    },
    OPEN_CONVERSATIONS: {
      display: 'open conversations',
      value(metric) { return metric.sum },
      previousValue(metric) { return metric.previousSum },
      delta(metric) { return DateHelpers.calculateChangePercentage(metric.previousSum, metric.sum) }
    },
    AVERAGE_TIME_TO_FIRST_RESPONSE: {
      display: 'avg time to first response',
      value(metric) { return DateHelpers.millisecondsToDuration(metric.average) },
      previousValue(metric) { return DateHelpers.millisecondsToDuration(metric.previousAverage) },
      dataTransformer(datum) { return parseFloat(DateHelpers.millisecondsToDuration(datum)) },
      delta(metric) { return DateHelpers.calculateChangePercentage(metric.previousAverage, metric.average) }
    },
    AVERAGE_TIME_TO_CLOSE: {
      display: 'avg time to close',
      value(metric) { return DateHelpers.millisecondsToDuration(metric.average) },
      previousValue(metric) { return DateHelpers.millisecondsToDuration(metric.previousAverage) },
      dataTransformer(datum) { return parseFloat(DateHelpers.millisecondsToDuration(datum)) },
      delta(metric) { return DateHelpers.calculateChangePercentage(metric.previousAverage, metric.average) }
    }
  }

const Dashboard = React.createClass({

  contextTypes: {
    location: PropTypes.object,
    router: PropTypes.object.isRequired
  },

  getInitialState() { return {
    columns: this.getColumns(),
    header: this.getHeader()
  } },

  getSelectedMetric() {
    const metrics = this.props.inboxStats != null ? this.props.inboxStats.inboxMetrics : undefined
    return _.find(metrics, { metricType: this.context.location.query.metric })
  },

  formatDateRangeTooltip(startDate, endDate) {
    const strFormat = 'MM/DD'
    return `${ startDate.format(strFormat) } - ${ endDate.format(strFormat) }`
  },

  getMetricTooltip(metric) {
    const metricDisplay = metricMapping[metric.metricType]
    const { query } = this.context.location
    const startDate = moment(+query.startDate)
    const endDate = moment(+query.endDate)

    const diff = this.getMomentDateRange().diff('days')

    const previousStartDate = moment(startDate).subtract(diff, 'days')
    const previousEndDate = moment(endDate).subtract(diff, 'days')

    return (
      <div>
        <table>
          <tr>
            <td>
              {this.formatDateRangeTooltip(previousStartDate, previousEndDate)}
            </td>
            <td>
              {metricDisplay.previousValue(metric)}
            </td>
          </tr>
          <tr>
            <td>
              {this.formatDateRangeTooltip(startDate, endDate)}
            </td>
            <td>
              {metricDisplay.value(metric)}
            </td>
          </tr>
        </table>
      </div>
    )
  },

  getMetricsItems() {
    return _.map(this.props.inboxStats != null ? this.props.inboxStats.inboxMetrics : undefined, (metric, i) => {
      const metricDisplay = metricMapping[metric.metricType]

      return (
        <div key={i} onClick={this.clickMetric.bind(this, metric)}>
          <CustomerMetric
            metric={(metricDisplay != null ? metricDisplay.value(metric) : undefined) || 0}
            display={metricDisplay.display}
            deltaPercentage={metricDisplay != null ? metricDisplay.delta(metric) : undefined}
            showDelta={true}
            selectable={true}
            tooltipText={this.getMetricTooltip(metric)}
            isSelected={metric === this.getSelectedMetric()} />
        </div>
      )
    })
  },

  cloneQuery() {
    return _.cloneDeep(this.context.location.query)
  },

  onQueryChange(query) {
    const pathname = '/conversation-stats'
    return this.context.router.push({ pathname, query })
  },

  clickMetric(metric) {
    if (this.context.location.metric === metric.metricType) { return }
    const query = this.cloneQuery()
    query.metric = metric.metricType
    return this.onQueryChange(query)
  },

  onSelectInbox(inbox) {
    if (this.context.location.query.inboxId === inbox.id) { return }
    const query = this.cloneQuery()
    query.inboxId = inbox.id
    return this.onQueryChange(query)
  },

  getMomentDateRange() {
    const { query } = this.context.location
    const now = moment()
    const startDate = query.startDate ? moment(parseInt(query.startDate)) : now
    const endDate = query.endDate ? moment(parseInt(query.endDate)) : now
    return moment.range(startDate, endDate)
  },

  onSetRange(range) {
    if (range.isSame(this.getMomentDateRange())) { return }
    const query = this.cloneQuery()
    query.startDate = range.start.valueOf()
    query.endDate = range.end.valueOf()
    return this.onQueryChange(query)
  },

  getColumns() {
    return [
      {
        property: 'name',
        header: 'Name'
      },
      {
        property: 'openConversations',
        header: 'Open Conversations'
      },
      {
        property: 'closedConversations',
        header: 'Closed Conversations'
      }
    ]
  },

  getHeader() {
    return {
      onClick: column => {
        return sortColumn(this.state.columns, column, this.setState.bind(this))
      }
    }
  },

  getTableData() {
    return _.map(this.props.inboxStats != null ? this.props.inboxStats.drifttUserMetrics : undefined, (userMetric, i) =>
      ({
        name: userMetric.user.name,
        openConversations: userMetric.openConversations,
        closedConversations: userMetric.closedConversations
      })
  )
  },

  getChartData() {
    const labels = []
    const numDays = this.getMomentDateRange().diff('days')

    this.getMomentDateRange().by('days', moment => labels.push(moment.format('MM/DD')))

    const selectedMetric = this.getSelectedMetric()
    const metricDisplay = metricMapping[__guard__(this.getSelectedMetric(), x => x.metricType)]

    const histogramData = (metricDisplay != null ? metricDisplay.dataTransformer : undefined) ? _.map(selectedMetric.buckets, metricDisplay.dataTransformer) : (selectedMetric != null ? selectedMetric.buckets : undefined)

    return {
      labels,
      datasets: [_.extend({ data: histogramData }, ColorConfig)]
    }
  },

  renderTables() {
    if ((this.props.inboxStats != null ? this.props.inboxStats.drifttUserMetrics.length : undefined) > 0) { return (
      <TabbedView
        tabs={[
          {
            display: 'By Team Member',
            render: () => <Table
              columns={this.state.columns}
              data={sortColumn.sort(this.getTableData(), this.state.sortingColumn, _.orderBy)}
              columnNames={this.state.header} />
          }
        ]} />
    ) }
  },

  render() {
    return (
      <div className="Page">
        <div className="DashboardPage">
          <div className="controls">
            <InboxButton
              onSelectInbox={this.onSelectInbox}
              selectedInbox={this.props.inboxesById != null ? this.props.inboxesById[this.context.location.query.inboxId] : undefined}
              inboxes={(this.props.inboxesById != null) && _.values(this.props.inboxesById)} />
            <TimeSelectorButton
              onSetRange={this.onSetRange}
              selectedDateRange={this.getMomentDateRange()} />
          </div>
          <div className="metrics">
            {this.getMetricsItems()}
          </div>
          <div className="chart-container">
            <LineChart redraw={true} options={ChartOptions} data={this.getChartData()} />
          </div>
          <div className="tabular-info">
            {this.renderTables()}
          </div>
        </div>
      </div>
    )
  }
})

export default Marty.createContainer(Dashboard, {
  contextTypes: {
    location: PropTypes.object
  },

  listenTo: ['stores.InboxStore', 'stores.DashboardStore'],

  fetch: {
    inboxesById() {
      return this.app.stores.InboxStore.getOrFetchInboxList()
    },

    inboxStats() {
      const { query } = this.props

      const { inboxId } = query
      const { startDate } = query
      const { endDate } = query
      if (inboxId) {
        return this.app.stores.DashboardStore.getOrFetchInboxStats(inboxId, startDate, endDate)
      }
    }
  },

  pending() {
    return <Dashboard />
  },

  failed(errors) {
    captureExceptions(errors)
    return <div />
  }
})

function __guard__(value, transform) {
  return (typeof value !== 'undefined' && value !== null) ? transform(value) : undefined
}



// WEBPACK FOOTER //
// ./src/scripts/components/ConversationStats/InboxMetrics.js